/**
 * Where a floating panel goes, and what it has to be inside to be seen.
 *
 * Both answers are the same for every popover in this app and neither is
 * obvious, which is why they are here rather than written out again each
 * time.
 */

/** Keep this much clear of the window's edges. */
const EDGE = 8;

/**
 * The element a panel must be appended to in order to be visible at all.
 *
 * A Bootstrap modal traps focus, so a panel outside it cannot be typed into;
 * a native `<dialog>` is in the top layer, which paints over everything
 * outside it whatever the z-index says. Both are answered by putting the
 * panel inside whichever of them owns the control it belongs to.
 */
export function popoverHost(anchor: Element): Element {
  return anchor.closest('dialog, .modal') ?? document.body;
}

/**
 * Put `panel` under `anchor`, or above it when there is no room below.
 *
 * The panel is `position: fixed`, so these are window coordinates and an
 * ancestor's scrolling does not enter into it. Clamped on both axes, because
 * a panel that opens half off-screen is a panel with no way to reach the
 * rest of it.
 */
export function placePopover(anchor: Element, panel: HTMLElement, gap = 4): void {
  const rect = anchor.getBoundingClientRect();
  const box = panel.getBoundingClientRect();
  const below = rect.bottom + gap;
  const top = below + box.height > window.innerHeight - EDGE
    ? Math.max(EDGE, rect.top - box.height - gap)
    : below;
  panel.style.top = `${top}px`;
  panel.style.left = `${Math.max(EDGE, Math.min(rect.left, window.innerWidth - box.width - EDGE))}px`;
}
